import { Controller, useFormContext } from "react-hook-form";
import ReactQuill from "react-quill";
import { Form } from "semantic-ui-react";

import type { Ref } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import type { FormFieldProps } from "semantic-ui-react";

type ShallowPath<T> = keyof T & string;

type TypeFormQuill<T extends FieldValues> = {
  name: ShallowPath<T>;
  quillRef?: Ref<ReactQuill>;
  formFieldProps?: FormFieldProps;
  quillProps?: ReactQuill.ReactQuillProps;
};

export const FormQuill = <T extends FieldValues>({ name, quillRef, formFieldProps, quillProps }: TypeFormQuill<T>) => {
  const methods = useFormContext<T>();

  const handleOnChange = (content: string, _delta: unknown, _source: unknown, editor: ReactQuill.UnprivilegedEditor, field: ControllerRenderProps<T, Path<T>>) => {
    const { ops } = editor.getContents();
    const isEmpty = !editor.getText().trim() && !ops?.some(op => typeof op.insert === "object");
    field.onChange(isEmpty ? "" : content);
  };

  return (
    <Controller
      name={name as Path<T>}
      {...methods}
      render={({ field, fieldState: { invalid, isTouched } }) => (
        <Form.Field {...formFieldProps} error={invalid && isTouched}>
          <ReactQuill
            {...field}
            {...quillProps}
            ref={quillRef ?? field.ref}
            value={field.value ?? ""}
            onChange={(content, delta, source, editor) => handleOnChange(content, delta, source, editor, field)}
          />
        </Form.Field>
      )}
    />
  );
};
